import { Component, model, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { OrganizationProfileHeaderComponent } from '../../components/organization-profile-header/organization-profile-header.component';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { OrganizationService } from '../../../../core/services/organization.service';
import { ProfileConstants } from '../../../../core/constants/profile.constants';
import { OrganizationProfileOverviewComponent } from '../../components/organization-profile-overview/organization-profile-overview.component';
import { OrganizationProfileSettingsComponent } from '../../components/organization-profile-settings/organization-profile-settings.component';
import { OrganizationProfileLogsComponent } from '../../components/organization-profile-logs/organization-profile-logs.component';
import { OrganizationContactsComponent } from '../../components/organization-contacts/organization-contacts.component';
import { OrganizationAddressesComponent } from '../../components/organization-addresses/organization-addresses.component';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { OrganizationPermission } from '../../../../core/enums/permissions.enums';
import { OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { OrganizationProfileNavItems } from '../../../../core/enums/nav-items.enum';
import { OrganizationSettingModel } from '../../../../core/models/data/organization-setting.model';
import { BaseProfilePageClass } from '../../../../core/base-classes/base-profile-page-class';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';

@Component({
  selector: 'ms-organization-profile.page',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    OrganizationProfileHeaderComponent,
    OrganizationProfileOverviewComponent,
    OrganizationProfileSettingsComponent,
    OrganizationProfileLogsComponent,
    OrganizationContactsComponent,
    OrganizationAddressesComponent,
    IfHasPermissionDirective
  ],
  templateUrl: './organization-profile.page.html',
  // Scopes every permission check beneath this page to the organization being viewed. This page is
  // reachable for any organization the viewer belongs to and does not change the active one, so
  // without this an Admin of the active organization appears to be an Admin here too (DEV-2643).
  providers: [ProfileOrganizationScopeService]
})
export class OrganizationProfilePage extends BaseProfilePageClass implements OnInit, OnDestroy {
  public organization = model<OrganizationModel | undefined>(undefined);
  public organizationSettings = model<OrganizationSettingModel | undefined>(undefined);
  public isLoading = signal<boolean>(false);
  public organizationId: string = "";

  public OrganizationProfileNavItems = OrganizationProfileNavItems;
  public OrganizationPermission = OrganizationPermission;

  public tab: string = "";

  private destroy$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private organizationService: OrganizationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.navItems.set(ProfileConstants.ORGANIZATION_PROFILE_NAV_ITEMS());

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.organizationId = params.get('organizationId') || "";
        // Set from the route before the fetch rather than from the response after it: the id is
        // already in the URL, so nothing on this page ever sees an unresolved scope.
        this.profileOrganizationScope?.set(Number(this.organizationId) || undefined);
        this.loadOrganizationProfile();
      });
      
    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe( params => {
        this.tab = params.get("tab") || "";
        if (this.tab != "") {
          this.setTabFromUrl(this.tab);
        }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadOrganizationProfile(): Promise<void> {
    this.isLoading.set(true);
    this.organization.set(undefined);
    this.organizationSettings.set(undefined);
    try {
      const organizationResponse = await this.organizationService.getOrganizationById(
        Number(this.organizationId),
        { includeContacts: true, includeAddresses: true }
      );

      const organizationSettingsResponse = await this.organizationService.getOrganizationSettings(
        Number(this.organizationId)
      );

      if (organizationResponse.data) {
        this.organization.set(organizationResponse.data);
        this.organizationSettings.set(organizationSettingsResponse.data);
        this.userSessionService.handleOrganizationUpdated(organizationResponse.data);
      } else {
        this.router.navigate(['system', '404']);
      }
    } catch (error) {
      console.error(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_LOADING_ORGANIZATION_PROFILE(), error);
      this.router.navigate(['system', '404']);
    } finally {
      this.isLoading.set(false);
    }
  }

  public async onOrganizationUpdated(): Promise<void> {
    await this.loadOrganizationProfile();
    this.setActiveTab(0);
  }

  public onStripeOnboardingComplete(): void {
    void this.loadOrganizationProfile();
    void this.userSessionService.refreshOrganizationSettings();
  }

  public onOrganizationSettingsUpdated(settings: OrganizationSettingModel): void {
    this.organizationSettings.set(settings);
  }

  public onOrganizationModelUpdated(updated: OrganizationModel): void {
    this.organization.set(updated);
    this.userSessionService.handleOrganizationUpdated(updated);
  }
}
